import axios from "axios";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import api from "@/services/api";
import type {
	CreateExpedientRequest,
	CreateVehicleOdometerChecklistRequest,
	CreateVehicleOdometerManualRequest,
	ExpedientDayApiItem,
	PreventiveMaintenance,
	PreventiveMaintenanceRequest,
	UpdateVehicleTelemetryRequest,
	VehicleAllListItem,
	VehicleApiResponse,
	VehicleForm,
	VehicleListParams,
	VehicleListResponse,
	VehicleOdometerCreateResponse,
	VehicleOdometerDetailResponse,
	VehicleOdometerHistoryParams,
	VehicleOdometerHistoryResponse,
	VehicleOdometerLatestChecklistResponse,
	VehicleOdometerPresignedUploadRequest,
	VehicleOdometerPresignedUploadResponse,
	VehicleOdometerSummaryResponse,
} from "../types";

dayjs.extend(customParseFormat);
const plainAxios = axios.create();

/** Converte dd/MM/yyyy para yyyy-MM-dd (ISO DateOnly). Se já for ISO ou inválido, retorna o valor. */
function toIsoDateOnly(value: string): string {
	const trimmed = value?.trim() ?? "";
	if (!trimmed) {
		return value;
	}
	const d = dayjs(trimmed, ["DD/MM/YYYY", "YYYY-MM-DD"], true);
	if (d.isValid()) {
		return d.format("YYYY-MM-DD");
	}
	return value;
}

export type VehicleStatsApiResponse = {
	total: number;
	available: number;
	inMaintenance: number;
	inactive: number;
};

export const vehiclesService = {
	async getStats(): Promise<VehicleStatsApiResponse> {
		const response =
			await api.get<VehicleStatsApiResponse>("/api/vehicle/stats");
		return response.data;
	},

	async getVehicles(params: VehicleListParams): Promise<VehicleListResponse> {
		const { page = 1, pageSize = 10, search } = params;

		const { data } = await api.get<VehicleListResponse>("/api/vehicle", {
			params: {
				PageNumber: page,
				PageSize: pageSize,
				...(search && { Plate: search }),
				Sort: "CreatedAt",
				SortDirection: "desc",
			},
		});

		return data;
	},

	async getVehicleById(id: string) {
		const { data } = await api.get<VehicleApiResponse>(`/api/vehicle/${id}`);
		return data;
	},

	async getAllVehicles(): Promise<VehicleAllListItem[]> {
		const { data } = await api.get<VehicleAllListItem[]>("/api/vehicle/all");
		return data;
	},

	async createVehicle(data: VehicleForm) {
		const { group_id, ...rest } = data;
		const body = { ...rest } as Record<string, unknown>;

		if (group_id !== undefined && group_id !== null) {
			body.groupId = group_id;
		}

		const response = await api.post<VehicleApiResponse>(
			"/api/vehicle/add",
			body
		);
		return response.data;
	},

	async updateVehicle(id: string, data: Partial<VehicleForm>) {
		const { group_id, ...rest } = data;
		const body = { ...rest } as Record<string, unknown>;

		if (group_id !== undefined) {
			body.groupId = group_id;
		}

		const response = await api.post<VehicleApiResponse>(
			`/api/vehicle/${id}/update`,
			body
		);
		return response.data;
	},

	async deleteVehicle(id: string): Promise<void> {
		await api.delete(`/api/vehicle/${id}`);
	},

	async updateVehicleStatus(id: string, status: number): Promise<void> {
		await api.put(`/api/vehicle/${id}/status`, { status });
	},

	async getPreventiveMaintenance(
		vehicleId: string
	): Promise<PreventiveMaintenance | null> {
		type RawPreventive = {
			id?: number;
			vehicleId?: number;
			dateOfLastPreventive: string;
			kilometersOfLastPreventive: number;
			dateOfNextPreventive: string;
		};
		try {
			const response = await api.get<RawPreventive>(
				`/api/maintenance/preventive/vehicle/${vehicleId}`
			);
			const data = response.data;
			if (!data) {
				return null;
			}
			return {
				id: String(data.id ?? ""),
				dateOfLastPreventive: data.dateOfLastPreventive,
				kilometersOfLastPreventive: data.kilometersOfLastPreventive,
				dateOfNextPreventive: data.dateOfNextPreventive,
			};
		} catch (err) {
			const status = (err as { response?: { status?: number } })?.response
				?.status;
			if (status === 404) {
				return null;
			}
			throw err;
		}
	},

	async createPreventiveMaintenance(
		vehicleId: string,
		data: PreventiveMaintenanceRequest
	): Promise<void> {
		const body = {
			dateOfLastPreventive: toIsoDateOnly(data.dateOfLastPreventive),
			kilometersOfLastPreventive: data.kilometersOfLastPreventive,
			dateOfNextPreventive: toIsoDateOnly(data.dateOfNextPreventive),
		};
		await api.post(`/api/maintenance/preventive/vehicle/${vehicleId}`, body);
	},

	async updatePreventiveMaintenance(
		preventiveId: string,
		data: PreventiveMaintenanceRequest
	): Promise<void> {
		const body = {
			dateOfLastPreventive: toIsoDateOnly(data.dateOfLastPreventive),
			kilometersOfLastPreventive: data.kilometersOfLastPreventive,
			dateOfNextPreventive: toIsoDateOnly(data.dateOfNextPreventive),
		};
		await api.put(`/api/maintenance/preventive/${preventiveId}`, body);
	},

	async getVehicleOdometerSummary(
		vehicleId: string
	): Promise<VehicleOdometerSummaryResponse> {
		const { data } = await api.get<VehicleOdometerSummaryResponse>(
			`/api/vehicles/${vehicleId}/odometer/summary`
		);
		return data;
	},

	async getVehicleOdometerHistory(
		vehicleId: string,
		params: VehicleOdometerHistoryParams
	): Promise<VehicleOdometerHistoryResponse> {
		const { data } = await api.get<VehicleOdometerHistoryResponse>(
			`/api/vehicles/${vehicleId}/odometer/history`,
			{
				paramsSerializer: {
					indexes: null,
				},
				params: {
					...(params.sources && params.sources.length > 0
						? { Sources: params.sources }
						: {}),
					PageNumber: params.pageNumber ?? 1,
					PageSize: params.pageSize ?? 5,
					...(params.sortBy ? { SortBy: params.sortBy } : {}),
					...(params.sortDirection
						? { SortDirection: params.sortDirection }
						: {}),
				},
			}
		);
		return data;
	},

	async getVehicleOdometerLatestChecklist(
		vehicleId: string
	): Promise<VehicleOdometerLatestChecklistResponse | null> {
		try {
			const { data } = await api.get<VehicleOdometerLatestChecklistResponse>(
				`/api/vehicles/${vehicleId}/odometer/checklist/latest`
			);
			return data;
		} catch (err) {
			const status = (err as { response?: { status?: number } })?.response
				?.status;
			if (status === 404) {
				return null;
			}
			throw err;
		}
	},

	async createVehicleOdometerChecklist(
		vehicleId: string,
		payload: CreateVehicleOdometerChecklistRequest
	): Promise<VehicleOdometerCreateResponse> {
		const { data } = await api.post<VehicleOdometerCreateResponse>(
			`/api/vehicles/${vehicleId}/odometer/checklist`,
			payload
		);
		return data;
	},

	async createVehicleOdometerManual(
		vehicleId: string,
		payload: CreateVehicleOdometerManualRequest
	): Promise<VehicleOdometerCreateResponse> {
		const { data } = await api.post<VehicleOdometerCreateResponse>(
			`/api/vehicles/${vehicleId}/odometer/manual`,
			payload
		);
		return data;
	},

	async getVehicleOdometerDetail(
		vehicleId: string,
		vehicleOdometerId: string
	): Promise<VehicleOdometerDetailResponse> {
		const { data } = await api.get<VehicleOdometerDetailResponse>(
			`/api/vehicles/${vehicleId}/odometer/${vehicleOdometerId}`
		);
		return data;
	},

	async getVehicleOdometerAttachmentPresignedUpload(
		vehicleId: string,
		payload: VehicleOdometerPresignedUploadRequest
	): Promise<VehicleOdometerPresignedUploadResponse> {
		const { data } = await api.post<VehicleOdometerPresignedUploadResponse>(
			`/api/vehicles/${vehicleId}/odometer/attachment/presigned-upload`,
			payload
		);
		return data;
	},

	async uploadToPresignedUrl(
		uploadUrl: string,
		file: File,
		contentType: string
	): Promise<void> {
		await plainAxios.put(uploadUrl, file, {
			headers: { "Content-Type": contentType },
		});
	},

	async getExpedient(vehicleId: string): Promise<ExpedientDayApiItem[]> {
		try {
			const response = await api.get<ExpedientDayApiItem[]>(
				`/api/expedient/${vehicleId}`
			);
			return response.data ?? [];
		} catch (err) {
			const status = (err as { response?: { status?: number } })?.response
				?.status;
			if (status === 404) {
				return [];
			}
			throw err;
		}
	},

	async createOrUpdateExpedient(data: CreateExpedientRequest): Promise<void> {
		await api.post("/api/expedient", data);
	},

	async updateVehicleTelemetry(
		vehicleId: string,
		payload: UpdateVehicleTelemetryRequest
	): Promise<void> {
		await api.put(`/api/vehicle/telemetry/${vehicleId}`, payload);
	},
};
